import React, { useState } from 'react';
import { Box, Typography, Paper, TextField, Button, Container, IconButton, Fade } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from './context/ThemeContext';
import { BrandLogo } from './components/BrandLogo';

export const MentorshipInquiry = () => {
  const navigate = useNavigate();
  const { mode } = useThemeContext();
  const isLight = mode === 'light';
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (phoneNumber.length >= 10) {
      setIsSubmitted(true);
      // Option 1: Direct Redirect to WhatsApp
      const adminNumber = '918340156311';
      const message = encodeURIComponent(`Hi FlowState, I am interested in 1:1 AI tutoring. My phone number is ${phoneNumber}.`);
      const whatsappUrl = `https://wa.me/${adminNumber}?text=${message}`;
      
      // Open WhatsApp in a new tab
      window.open(whatsappUrl, '_blank');
      
      console.log('Tutoring inquiry sent to WhatsApp for:', phoneNumber);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative Elements (replicating the 3D shapes in screenshot) */}
      <Box sx={{
        position: 'absolute',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        border: '40px solid rgba(255,255,255,0.1)',
        top: '-100px',
        left: '10%',
        zIndex: 0
      }} />
      <Box sx={{
        position: 'absolute',
        width: '150px',
        height: '150px',
        bgcolor: 'rgba(255,255,255,0.05)',
        transform: 'rotate(45deg)',
        bottom: '10%',
        left: '5%',
        zIndex: 0
      }} />

      {/* Left Side: Marketing Content */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        px: { xs: 4, md: 10 },
        py: { xs: 8, md: 0 },
        zIndex: 1,
        color: '#fff'
      }}>
        <Box sx={{ mb: 6, cursor: 'pointer', display: 'inline-block' }} onClick={() => navigate('/interview-prep')}>
          <BrandLogo size="large" isLight={false} />
        </Box>
        
        <Typography variant="h1" sx={{ 
          fontSize: { xs: '2.5rem', md: '4.5rem' }, 
          fontWeight: 900, 
          lineHeight: 1, 
          mb: 2,
          letterSpacing: '-2px'
        }}>
          1:1 AI Tutoring
        </Typography>
        <Typography variant="h3" sx={{ 
          fontSize: { xs: '1.5rem', md: '2.5rem' }, 
          fontWeight: 700, 
          mb: 3,
          opacity: 0.9 
        }}>
          Tailored for Success
        </Typography>
        <Typography sx={{ 
          fontSize: '1.1rem', 
          maxWidth: 500, 
          opacity: 0.8,
          lineHeight: 1.6,
          fontWeight: 500
        }}>
          Unlock your dream career through a personalized mentorship program designed for aspiring AI engineers and full-stack developers.
        </Typography>
      </Box>

      {/* Right Side: Inquiry Form Card */}
      <Box sx={{
        flex: { md: '0 0 500px' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        zIndex: 1,
        background: { xs: 'transparent', md: 'rgba(255,255,255,0.05)' },
        backdropFilter: { md: 'blur(10px)' }
      }}>
        <Paper elevation={24} sx={{
          p: { xs: 4, md: 6 },
          width: '100%',
          maxWidth: 440,
          borderRadius: 6,
          bgcolor: isLight ? 'rgba(255, 255, 255, 0.95)' : '#111113',
          textAlign: 'center',
          boxShadow: '0 40px 100px -20px rgba(0,0,0,0.5)'
        }}>
          {!isSubmitted ? (
            <Fade in={!isSubmitted}>
              <Box component="form" onSubmit={handleSubmit}>
                <Typography variant="h4" fontWeight="800" gutterBottom sx={{ color: isLight ? '#0f172a' : '#fff' }}>
                  Get in Touch
                </Typography>
                <Typography variant="body1" sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 4 }}>
                  Enter your phone number below and our expert mentors will reach out to you within 24 hours.
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Your Phone Number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  InputProps={{
                    startIcon: <PhoneIcon sx={{ mr: 1, color: '#3b82f6' }} />,
                    sx: {
                      borderRadius: 3,
                      bgcolor: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      py: 0.5
                    }
                  }}
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={phoneNumber.length < 10}
                  sx={{
                    py: 2,
                    borderRadius: 3,
                    fontSize: '1rem',
                    fontWeight: 800,
                    textTransform: 'none',
                    bgcolor: '#2563eb',
                    '&:hover': { bgcolor: '#1d4ed8' },
                    boxShadow: '0 10px 25px -5px rgba(37, 99, 235, 0.4)'
                  }}
                >
                  Request Callback
                </Button>

                <Typography variant="caption" sx={{ display: 'block', mt: 3, color: isLight ? '#94a3b8' : '#52525b' }}>
                  By clicking, you agree to FlowState's Terms of Service and Privacy Policy.
                </Typography>
              </Box>
            </Fade>
          ) : (
            <Fade in={isSubmitted}>
              <Box sx={{ py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
                <Typography variant="h4" fontWeight="800" sx={{ color: isLight ? '#0f172a' : '#fff', mb: 2 }}>
                  Request Received!
                </Typography>
                <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 4 }}>
                  One of our mentors will contact you shortly on <strong>{phoneNumber}</strong> to discuss your career goals.
                </Typography>
                <Button 
                  onClick={() => navigate('/interview-prep')}
                  fullWidth
                  variant="outlined"
                  sx={{ py: 1.5, borderRadius: 3, fontWeight: 700 }}
                >
                  Back to Dashboard
                </Button>
              </Box>
            </Fade>
          )}
        </Paper>
      </Box>

      {/* Back Button */}
      <IconButton 
        onClick={() => navigate(-1)}
        sx={{ 
          position: 'absolute', 
          top: 20, 
          right: 20, 
          color: '#fff',
          bgcolor: 'rgba(255,255,255,0.1)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
        }}
      >
        <ArrowBackIcon />
      </IconButton>
    </Box>
  );
};
